import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography, CircularProgress, Button, Tabs, Tab, Box } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { Favorite, FavoriteBorder } from '@mui/icons-material';

import placeholderItem from 'styles/assets/placeholder/placeholder.gif';

import { Slide } from 'react-slideshow-image';
import { toast } from 'react-toastify';

import { getUrlVariables, trackEventMatomo, trackEventMatomoVisit } from 'js/library/utils/helpers';
import { useCurrentUserInfo } from 'js/library/services/currentUserInfo';
import { downloadImage } from 'js/library/services/StorageManager.js';

import { getEstablishmentItem } from 'js/library/utils/API/getEstablishmentItem';
import { getProducts } from 'js/library/utils/API/getProducts';
import { getPromotions } from 'js/library/utils/API/getPromotions';
import { getUserData } from 'js/library/utils/API/getUserData';
import addFavorite from 'js/library/utils/API/addFavorite';
import deleteFavorite from 'js/library/utils/API/deleteFavorite';

import GoBack from 'js/containers/GoBack.jsx';
import CuponsComponent from './CuponsComponent';
import Products from './Products';
import Ratings from './Rating';
import PlaceAbout from './PlaceAbout';

import 'react-slideshow-image/dist/styles.css';
import './placesStyles.css';
import cfac22 from 'js/library/utils/cfac22';

const Places = ({ location, setOpenState }) => {
  const { t } = useTranslation('app');

  const [establishmentInfo, setEstablishmentInfo] = useState(null);
  const [listaFotosDownloaded, setListaFotosDownloaded] = useState([]);
  const [productList, setProductList] = useState(null);
  const [promotionList, setPromotionList] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState('0');

  const { userInfo, loading: userInfoLoading } = useCurrentUserInfo();
  const getVars = getUrlVariables();
  const id = getVars.id;
  const [placeId, setPlaceId] = useState(id);

  const isGuest = userInfo === null || userInfo?.triiboId?.indexOf('@guest-triibo.com.br') >= 0;

  // ...
  const getEstablishmentData = useCallback(
    async (dadosEstabelecimento) => {
      if (!dadosEstabelecimento || !userInfo?.uId) return;

      if (Array.isArray(dadosEstabelecimento.listaFotos)) {
        dadosEstabelecimento.listaFotos.forEach((foto, i) => {
          downloadImage('estabelecimento', foto)
            .then((result) => {
              setListaFotosDownloaded((prev) => {
                const novaLista = [...prev];
                novaLista[i] = result;
                return novaLista;
              });
            })
            .catch(() => {});
        });
      }

      try {
        const payload = {
          systemQuery: {
            grupo1: [
              {
                field: 'establishmentId',
                type: '==',
                value: id,
              },
            ],
          },
          productType: ['digital', 'physical'],
          orderType: 'relevance',
          plataform: 'web',
          from: 0,
          size: 200,
          partnerId: cfac22('partnerId'),
        };

        const [promotions, products] = await Promise.all([
          getPromotions(userInfo.uId, payload),
          getProducts(userInfo.uId, payload),
        ]);

        setTabIndex(products.length > 0 ? '0' : promotions.length > 0 ? '1' : '2');
        setPromotionList(promotions);
        setProductList(products);
      } catch (error) {
        console.log('error', error);
        toast.error(error);
        setPromotionList([]);
        setProductList([]);
      }

      const checkFavoriteUser = (establishmentInfo) => {
        setLoading(true);

        getUserData({
          identifier: userInfo.uId,
          partnerId: cfac22('partnerId'),
          type: 'uid',
        })
          .then((result) => {
            setLoading(false);
            const favoritos = result?.favorites || [];
            const isFav = favoritos.some((fav) => fav.id === establishmentInfo.id);
            setIsFavorite(isFav);
          })
          .catch(() => {
            toast.error(t('errors.try_later'));
            setLoading(false);
          });
      };

      if (!isGuest) {
        checkFavoriteUser(dadosEstabelecimento);
      }
    },
    [userInfo, isGuest, t, id]
  );

  useEffect(() => {
    const updatePlace = (newId) => {
      setEstablishmentInfo(null);
      setProductList(null);
      setPromotionList(null);
      setListaFotosDownloaded([]);
      setPlaceId(newId);

      if (!userInfo?.uId) return;
      getEstablishmentItem(newId).then((res) => {
        if (res) {
          setEstablishmentInfo(res.establishmentInfo);
          getEstablishmentData(res.establishmentInfo);
        } else {
          setEstablishmentInfo(false);
        }
      });
    };

    if (getUrlVariables().id !== placeId && establishmentInfo !== null) {
      updatePlace(getUrlVariables().id);
    }
  }, [placeId, establishmentInfo, userInfo, getEstablishmentData]);

  const fetchEstablishment = useCallback(() => {
    const infoEstabelecimento = location?.state || null;
    if (!userInfo?.uId) return;

    if (infoEstabelecimento === null || infoEstabelecimento.cardId !== undefined) {
      getEstablishmentItem(id).then((res) => {
        if (res) {
          setEstablishmentInfo(res.establishmentInfo);
          getEstablishmentData(res.establishmentInfo);
        } else {
          setEstablishmentInfo(false);
        }
      });
    } else {
      setEstablishmentInfo(infoEstabelecimento);
      getEstablishmentData(infoEstabelecimento);
    }
  }, [id, location, userInfo, getEstablishmentData]);

  useEffect(() => {
    if (!userInfo?.uId) return;

    document.title = 'Estabelecimento';
    trackEventMatomoVisit('Estabelecimento');
    window.scrollTo(0, 0);
    fetchEstablishment();
  }, [fetchEstablishment, userInfo]);

  const checkAnonymous = () => {
    setOpenState(true);
  };

  const handleFavorite = async () => {
    if (isGuest) {
      checkAnonymous();
      return;
    }

    try {
      if (isFavorite) {
        await deleteFavorite(userInfo.uId, establishmentInfo.id, 'establishment');
        toast.success(t('general.removed_successfully'));
      } else {
        await addFavorite(userInfo.uId, establishmentInfo.id, 'establishment');
        toast.success(t('general.added_successfully'));
      }
      setIsFavorite(!isFavorite);
    } catch (_err) {
      toast.error(t('errors.try_later'));
    }
  };

  const downloadImageOnLoad = (type, i) => {
    const list = type === 'productList' ? productList : promotionList;
    let folder = '',
      thumb = '';

    if (type === 'productList') {
      const parts = list[i].thumbnail.split('/');
      folder = parts[0];
      thumb = parts[1];
    } else {
      folder = 'promocao';
      thumb = list[i].thumbnail;
    }

    downloadImage(folder, thumb)
      .then((result) => {
        const novo = [...list];
        novo[i].downloadedThumbnail = result;
        if (type === 'productList') {
          setProductList(novo);
        } else {
          setPromotionList(novo);
        }
      })
      .catch(() => {});
  };

  const handleChangeTabs = (_event, newValue) => {
    setTabIndex(newValue);
  };

  const properties = {
    duration: 5000,
    transitionDuration: 500,
    infinite: false,
    indicators: true,
    arrows: true,
    pauseOnHover: true,
  };

  if (userInfoLoading || !userInfo?.uId) {
    return <CircularProgress />;
  }

  return (
    <div>
      {establishmentInfo === null || establishmentInfo.cardId !== undefined ? (
        <div className="loading-content" style={{ textAlign: 'center' }}>
          <CircularProgress />
        </div>
      ) : establishmentInfo === false ? (
        <div style={{ textAlign: 'center' }}>
          <Typography variant="h6">{t('general.did_not_find_were_looking_for')}</Typography>
          <Button
            onClick={() => window.history.back()}
            variant="contained"
            color="primary"
            style={{ textTransform: 'none' }}
          >
            {t('general.back')}
          </Button>
        </div>
      ) : (
        <div>
          {/* HEADER */}
          <div className="place-header">
            <GoBack />
            <span>{establishmentInfo.nome}</span>
            {loading ? (
              <CircularProgress size={20} />
            ) : isFavorite ? (
              <Favorite
                className="iconHeart"
                style={{ color: '#6E3296' }}
                onClick={() => {
                  handleFavorite();
                  trackEventMatomo('Estabelecimento', 'tocar', 'botao', 'Desfavoritar');
                }}
              />
            ) : (
              <FavoriteBorder
                className="iconHeart"
                style={{ color: '#6E3296' }}
                onClick={() => {
                  handleFavorite();
                  trackEventMatomo('Estabelecimento', 'tocar', 'botao', 'Favoritar');
                }}
              />
            )}
          </div>

          {/* SLIDER */}
          <div className="slider-home">
            <Slide {...properties}>
              {establishmentInfo.listaFotos?.length > 0 ? (
                establishmentInfo.listaFotos.map((_, i) => (
                  <div key={i} className="each-slide">
                    <img
                      width="100%"
                      src={listaFotosDownloaded[i] || placeholderItem}
                      alt={`slide-${i}`}
                    />
                  </div>
                ))
              ) : (
                <img width="100%" src={placeholderItem} alt="placeholder" />
              )}
            </Slide>
          </div>

          {/* TABS */}
          {promotionList && productList && (
            <TabContext value={tabIndex}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={tabIndex}
                  onChange={handleChangeTabs}
                  sx={{
                    padding: '0 12px',
                    background: '#fff',
                    '& button:hover': { background: '#dadada' },
                    '& button.Mui-selected': {
                      color: '#6E3296',
                    },
                  }}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: '#6E3296',
                    },
                  }}
                  centered
                  variant={window.innerWidth < 600 ? 'fullWidth' : 'standard'}
                >
                  {productList && productList.length > 0 && (
                    <Tab className="tabs-title" label={t('general.store')} value={'0'} />
                  )}
                  {promotionList && promotionList.length > 0 && (
                    <Tab className="tabs-title" label={t('general.coupons')} value={'1'} />
                  )}
                  <Tab
                    className="tabs-title"
                    onClick={() =>
                      trackEventMatomo(`estabelecimento/?id=${id}`, 'tocar', 'tabs', 'Sobre')
                    }
                    label={t('general.about')}
                    value={'2'}
                  />
                  <Tab
                    className="tabs-title"
                    onClick={() =>
                      trackEventMatomo(`estabelecimento/?id=${id}`, 'tocar', 'tabs', 'Avaliação')
                    }
                    label={t('general.rating')}
                    value={'3'}
                  />
                </Tabs>
              </Box>

              {productList && productList.length > 0 && (
                <TabPanel value={'0'}>
                  <Products
                    userInfo={userInfo}
                    productList={productList}
                    downloadImageOnLoad={downloadImageOnLoad}
                  />
                </TabPanel>
              )}
              {promotionList && promotionList.length > 0 && (
                <TabPanel value={'1'}>
                  <CuponsComponent
                    promotionList={promotionList}
                    downloadImageOnLoad={downloadImageOnLoad}
                  />
                </TabPanel>
              )}
              <TabPanel value={'2'}>
                <PlaceAbout
                  checkAnonymous={checkAnonymous}
                  userInfo={userInfo}
                  establishmentInfo={establishmentInfo}
                />
              </TabPanel>
              <TabPanel value={'3'}>
                <Ratings
                  checkAnonymous={checkAnonymous}
                  establishmentInfo={establishmentInfo}
                  userInfo={userInfo}
                />
              </TabPanel>
            </TabContext>
          )}
        </div>
      )}
    </div>
  );
};

export default Places;
